import { CalendarCheck, Home, ChefHat, Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import useScrollReveal from "@/hooks/useScrollReveal";

const ProcessSection = () => {
  const sectionRef = useScrollReveal();

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const steps = [
    {
      number: 1,
      icon: CalendarCheck,
      title: "Termin vereinbaren",
      description: "Kontaktiere mich – wir finden einen passenden Termin.",
    },
    {
      number: 2,
      icon: Home,
      title: "Ich komme zu dir",
      description: "Ich bringe alles mit. Du musst nichts vorbereiten.",
    },
    {
      number: 3,
      icon: ChefHat,
      title: "Gemeinsam kochen",
      description: "Wir kochen zusammen und du erlebst den Thermomix live.",
    },
    {
      number: 4,
      icon: Heart,
      title: "Du entscheidest",
      description: "Ohne Druck – ob der Thermomix zu dir passt, entscheidest du.",
    },
  ];

  return (
    <section
      id="process"
      data-testid="process-section"
      className="section-padding bg-white relative overflow-hidden"
      ref={sectionRef}
    >
      <div className="container-custom">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-20 reveal">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            So läuft's ab
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 font-[Outfit]">
            In 4 Schritten zu deinem Thermomix® in Krefeld
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Unkompliziert, persönlich und völlig unverbindlich.
          </p>
        </div>

        {/* Steps - Horizontal on desktop */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 mb-20 stagger-children" ref={sectionRef}>
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                data-testid={`process-step-${index}`}
                className="relative text-center group"
              >
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-emerald-200 via-emerald-100 to-transparent" />
                )}

                {/* Step Card */}
                <div className="glass-card rounded-[2rem] p-8 hover-lift h-full">
                  {/* Step Number */}
                  <div className="step-number mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-5">
                    <IconComponent className="w-7 h-7 text-slate-600" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-slate-900 mb-3 font-[Outfit]">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Elements */}
        <div className="grid sm:grid-cols-3 gap-5 max-w-3xl mx-auto mb-16 reveal">
          {[
            { label: "Kostenlos", sublabel: "Keine versteckten Kosten" },
            { label: "Unverbindlich", sublabel: "Kein Kaufzwang" },
            { label: "Persönlich", sublabel: "Individuelle Beratung" },
          ].map((item, index) => (
            <div
              key={index}
              data-testid={`trust-element-${index}`}
              className="text-center p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-100"
            >
              <div className="text-xl font-bold text-emerald-700 font-[Outfit]">
                {item.label}
              </div>
              <div className="text-sm text-emerald-600 mt-1">{item.sublabel}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center reveal">
          <Button
            onClick={scrollToContact}
            data-testid="process-cta-button"
            className="btn-primary px-10 py-7 text-lg group"
          >
            Jetzt kostenlos anfragen
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
